import React from 'react'
import { reduxForm, Field } from 'redux-form'
import { Segment } from '../common/Layout'
import { Action, Button, InputError } from '@foodsby/nutrient'
import Input from '../common/reduxForms/Input'
import ModalPopup from '../common/ModalPopup'
import { confirmation, length, required } from 'redux-form-validators'
import { Submitting } from '../common/Submitting'
import { updatePersonalInfoStart } from '../../redux/modules/profile'
import { SimpleSpinner } from '../common/SimpleSpinner'
import { formatTenDigitPhoneNumber } from '../../util/formatUtils'
import { noSpacesAllowed } from '../../util/validators'
import SaveAndCancelButtons from './SaveAndCancelButtons'
import { ProfileImage } from './ProfilePicture'
import { normalizeProfileImageErrors } from '../../util/errorUtils'

export const PersonalInformationPanel = ({
  profile: { firstName, lastName, email, phone, username, avatarId },
  isEditingPersonalInfo,
  changePersonalInfoEditingMode,
  isChangingPassword,
  changePasswordEditingMode,
  changePasswordStart,
  updatePersonalInfoStart,
  submitting,
  success,
  changePasswordFailed,
  isCurrentUserLoading,
  updateProfilePictureStart,
  updateProfilePictureError,
  updateProfilePictureLoading,
  isWebpSupported,
}) => {
  return (
    <Segment>
      <div className="HeaderSection">
        <div>
          <h6 className="HeaderSection-heading">Personal Information</h6>
        </div>
        <div>
          {isEditingPersonalInfo ? (
            <SaveAndCancelButtons
              changePersonalInfoEditingMode={changePersonalInfoEditingMode}
              formName={'personalInfo'}
              submitting={submitting}
            />
          ) : (
            <EditButton
              changePersonalInfoEditingMode={changePersonalInfoEditingMode}
            />
          )}
        </div>
      </div>

      {/* End  Card Header*/}

      <div className="row">
        <div className="profile-image-container col-sm-5 center-xs col-xs-12">
          <ProfileImage
            avatarId={avatarId}
            isWebpSupported={isWebpSupported}
            name={firstName && lastName && `${firstName} ${lastName}`}
            updateProfilePictureLoading={updateProfilePictureLoading}
            updateProfilePictureStart={updateProfilePictureStart}
          />
          {updateProfilePictureError && (
            <InputError>
              {normalizeProfileImageErrors(
                updateProfilePictureError.message || '',
              )
                .slice(0, 1)
                .map((error, i) => (
                  <div key={i}>{error}</div>
                ))}
            </InputError>
          )}
        </div>
        <div className="col-xs-12 col-sm-7">
          {isEditingPersonalInfo ? (
            <ReduxEditPersonalInformationForm
              changePersonalInfoEditingMode={changePersonalInfoEditingMode}
              initialValues={{
                firstName,
                lastName,
                email,
                phone,
              }}
              onSubmit={updatePersonalInfoStart}
              submitting={submitting}
            />
          ) : (
            <ProfileNamePanel
              changePersonalInfoEditingMode={changePersonalInfoEditingMode}
              email={email}
              firstName={firstName}
              isCurrentUserLoading={isCurrentUserLoading}
              lastName={lastName}
              phone={phone}
            />
          )}
          <ChangePassword
            changePasswordEditingMode={changePasswordEditingMode}
            changePasswordStart={changePasswordStart}
            email={username}
            failure={changePasswordFailed}
            isChangingPassword={isChangingPassword}
            submitting={submitting}
            success={success}
          />
        </div>
      </div>
    </Segment>
  )
}

PersonalInformationPanel.defaultProps = {
  profile: {},
}

export const EditButton = ({ changePersonalInfoEditingMode }) => (
  <Action
    className="secondary change-name"
    onClick={() => changePersonalInfoEditingMode(true)}
  >
    Edit Profile
  </Action>
)

export const ProfileNamePanel = ({
  firstName,
  lastName,
  phone,
  isCurrentUserLoading,
}) => (
  <>
    {isCurrentUserLoading ? (
      <SimpleSpinner />
    ) : (
      <div>
        <div className="row">
          <div className="col-xs-6">
            <div className="Input is-inactive">
              <label htmlFor="firstName">First Name</label>
              <p>{firstName == null ? 'No First Name Provided' : firstName}</p>
            </div>
          </div>
          <div className="col-xs-6">
            <div className="Input is-inactive">
              <label htmlFor="lastName">Last Name</label>
              <p className="ProCard">
                {lastName == null ? 'No Last Name Provided' : lastName}
              </p>
            </div>
          </div>
        </div>
        <div className="Input is-inactive">
          <label className="ProCard-label" htmlFor="phone">
            Phone Number
          </label>
          <p className="ProCard-text">
            {formatTenDigitPhoneNumber(phone) == null
              ? 'No Phone Number Provided'
              : formatTenDigitPhoneNumber(phone)}
          </p>
        </div>

        {/**
          * Waiting on service to be created
          *
          <div className="row">
          <div className="col-xs-12">
            <label htmlFor="birthDay" className="ProCard-label">
              Birth Date (Optional)
            </label>
            <p className="ProCard-text">
              {firstName == null ? 'No Birth Date Provided' : firstName}
            </p>
          </div>
        </div> */}
      </div>
    )}
  </>
)

export const EditPersonalInformationForm = ({ handleSubmit }) => (
  <form onSubmit={handleSubmit}>
    <div className="row">
      <div className="col-xs-6">
        <div className="Input">
          <Field
            className="ProCard-inputLabel"
            component={Input}
            id="firstName"
            label="First Name"
            maxLength="35"
            name="firstName"
            type="text"
            validate={[
              noSpacesAllowed,
              required({ msg: `First Name is required` }),
            ]}
          />
        </div>
      </div>
      <div className="col-xs-6">
        <div className="Input">
          <Field
            className="ProCard-inputLabel"
            component={Input}
            id="lastName"
            label="Last Name"
            maxLength="35"
            name="lastName"
            type="text"
            validate={[
              noSpacesAllowed,
              required({ msg: `Last Name is required` }),
            ]}
          />
        </div>
      </div>
    </div>
    <div className="Input">
      <Field
        className="ProCard-inputLabel"
        component={Input}
        id="phone"
        label="Phone Number"
        name="phone"
        normalize={val => val.replace(/\D/g, '')}
        props={{
          mask: '_',
          format: '(###) ###-####',
        }}
        type="tel"
        validate={[
          noSpacesAllowed,
          required({ msg: `Phone Number is required` }),
          length({
            is: 10,
            msg: { wrongLength: `Phone Number must be 10 digits` },
          }),
        ]}
      />
    </div>
  </form>
)

export const ReduxEditPersonalInformationForm = reduxForm({
  form: 'personalInfo',
  onSubmit: updatePersonalInfoStart,
})(EditPersonalInformationForm)

export const ChangePasswordModal = ({
  changePasswordEditingMode,
  handleSubmit,
  submitting,
  failure,
  pristine,
  invalid,
}) => (
  <form className="WebConV3" onSubmit={handleSubmit}>
    <div className="Input">
      <Field
        component={Input}
        id="oldPassword"
        label="Current Password"
        name="oldPassword"
        type="password"
        validate={[
          required({ msg: `Current Password is required` }),
          length({
            minimum: 6,
            msg: 'Current Password is too short',
          }),
        ]}
      />
    </div>
    <div className="Input">
      <Field
        component={Input}
        id="password"
        label="New Password"
        name="password"
        type="password"
        validate={[
          noSpacesAllowed,
          required({ msg: `New Password is required` }),
          length({ minimum: 8, msg: 'New Password is too short' }),
        ]}
      />
    </div>
    <div className="Input">
      <Field
        component={Input}
        id="confirmPassword"
        label="Confirm Password"
        name="confirmPassword"
        type="password"
        validate={[
          noSpacesAllowed,
          confirmation({
            field: 'password',
            fieldLabel: 'New Password',
            msg: 'Password does not match New Password',
          }),
        ]}
      />
    </div>
    {failure ? <FailureMessage /> : null}
    <div className="row middle-xs">
      <div className="col-xs center-xs">
        <Action
          className="cancel"
          onClick={() => changePasswordEditingMode(false)}
          type="button"
        >
          Cancel
        </Action>
      </div>
      <div className="col-xs center-xs">
        {submitting ? (
          <Submitting />
        ) : (
          <Button disabled={invalid || pristine} type="submit">
            Save Changes
          </Button>
        )}
      </div>
    </div>
  </form>
)

export const ChangePasswordForm = reduxForm({
  form: 'changePassword',
})(ChangePasswordModal)

export const ChangePassword = ({
  isChangingPassword,
  changePasswordEditingMode,
  changePasswordStart,
  success,
  failure,
  email,
}) => (
  <>
    <div className="Input is-inactive">
      <label className="ProCard-label" htmlFor="email">
        Email
      </label>
      <p className="ProCard-text">{email}</p>
    </div>
    <div className="row">
      <div className="col-xs-12 col-md-6">
        <Action onClick={() => changePasswordEditingMode(true)}>
          Change Password
        </Action>
        {isChangingPassword ? (
          <ModalPopup showCancel={false}>
            <ChangePasswordForm
              changePasswordEditingMode={changePasswordEditingMode}
              failure={failure}
              onSubmit={changePasswordStart}
            />
          </ModalPopup>
        ) : null}
        {success ? <SuccessMessage /> : null}
      </div>
    </div>
  </>
)

const SuccessMessage = () => (
  <span className="success col-xs-12">
    <i className="icon-check-circle success" />
    Your Password has been successfully changed.
  </span>
)

const FailureMessage = () => (
  <span className="error col-xs-12">
    Sorry your password was not changed, please verify the information entered
    and try again.
  </span>
)

export default PersonalInformationPanel
